import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Box, Text } from 'grommet';
import { useAppTheme } from '@Components/Context';
import {
  Seo, InfoNotice, AppButton, SimpleModal, FlexibleSelectDropdown,
} from '@Components/Control';
import { AdminNavWrapper } from '@Components/Navigation';
import {
  initiateFetchCidPayoutSetupStatus,
  clearCidPayoutSetupStatus,
} from '@Actions';
import {
  TableWrapper,
  NoDataPlaceholder,
  tablePadding,
  renderTableHeader,
} from '../../../Partial/Product/SharedComponents';

const HEADER = [
  { display: 'Company', uid: 'companyName', size: '22%' },
  { display: 'Payout Setup', uid: 'stripeSetup', size: '12%' },
  { display: 'Stripe Account ID', uid: 'stripeAccountId', size: '22%' },
  { display: 'Asset Labels', uid: 'assetLabels', size: '28%' },
  { display: 'CID Enabled', uid: 'featureEnabledAt', size: '16%' },
];

const STATUS_OPTIONS = [
  { api: 'complete', display: 'Setup Complete' },
  { api: 'incomplete', display: 'Incomplete' },
  { api: 'pending', display: 'Pending' },
];

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });
}

const AdminCidPayoutStatusPage = ({
  small,
  location,
  fetchCidPayoutStatus,
  clearCidPayoutStatus,
  loading,
  cidPayoutStatus = null,
}) => {
  const { theme: { page, button }, isDarkMode } = useAppTheme();
  const usePageBg = isDarkMode ? page.background : page.altBg;

  const STATUS_CONFIG = {
    complete: { label: 'Setup Complete', color: page.statusSuccess },
    incomplete: { label: 'Incomplete', color: '#f6ad55' },
    pending: { label: 'Pending', color: page.secondaryText },
  };

  const [searchQuery, setSearchQuery] = React.useState('');
  const [selectedStatus, setSelectedStatus] = React.useState('');
  const [labelsBreakdownRow, setLabelsBreakdownRow] = React.useState(null);

  React.useEffect(() => {
    fetchCidPayoutStatus();
    return () => { clearCidPayoutStatus(); };
  }, [fetchCidPayoutStatus]);

  const rows = cidPayoutStatus || [];

  const filteredRows = React.useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return rows.filter((r) => {
      if (selectedStatus && r.stripePayoutStatus !== selectedStatus) return false;
      if (q) {
        return r.companyName?.toLowerCase().includes(q)
          || String(r.companyId).includes(q)
          || (r.stripeAccountId || '').toLowerCase().includes(q);
      }
      return true;
    });
  }, [rows, selectedStatus, searchQuery]);

  const totalCompanies = rows.length;
  const { completeCount, incompleteCount, pendingCount } = rows.reduce(
    (acc, r) => {
      if (r.stripePayoutStatus === 'complete') acc.completeCount += 1;
      else if (r.stripePayoutStatus === 'incomplete') acc.incompleteCount += 1;
      else acc.pendingCount += 1;
      return acc;
    },
    { completeCount: 0, incompleteCount: 0, pendingCount: 0 },
  );

  const renderCell = (uid, row) => {
    if (uid === 'companyName') {
      return (
        <Box direction="column" gap="0.2rem">
          <Text size="1rem" color={page.tableText}>{row.companyName}</Text>
          <Text size="0.75rem" color={page.tableHeader}>{`ID: ${row.companyId}`}</Text>
        </Box>
      );
    }

    if (uid === 'stripeSetup') {
      const { label, color } = STATUS_CONFIG[row.stripePayoutStatus] || STATUS_CONFIG.pending;
      return (
        <Box
          background={`${color}22`}
          border={{ color, size: '1px' }}
          round="6px"
          pad={{ vertical: '0.2rem', horizontal: '0.5rem' }}
          width="fit-content"
        >
          <Text size="0.8rem" weight={600} color={color}>
            {label}
          </Text>
        </Box>
      );
    }

    if (uid === 'stripeAccountId') {
      if (!row.stripeAccountId) {
        return <Text size="0.9rem" color={page.hintText}>Not set</Text>;
      }
      return (
        <Text size="0.85rem" color={page.tableText} style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
          {row.stripeAccountId}
        </Text>
      );
    }

    if (uid === 'assetLabels') {
      const { assetLabelCount = 0 } = row;
      if (assetLabelCount === 0) {
        return <Text size="0.9rem" color={page.hintText}>No labels</Text>;
      }
      return (
        <Text
          size="0.9rem"
          color={page.highlight}
          style={{ textDecoration: 'underline', cursor: 'pointer' }}
          role="button"
          tabIndex={0}
          onClick={() => setLabelsBreakdownRow(row)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setLabelsBreakdownRow(row);
            }
          }}
        >
          {`${assetLabelCount} label${assetLabelCount !== 1 ? 's' : ''}`}
        </Text>
      );
    }

    if (uid === 'featureEnabledAt') {
      return (
        <Text size="0.9rem" color={page.tableText}>
          {formatDate(row.featureEnabledAt)}
        </Text>
      );
    }

    return <Text size="1rem" color={page.tableText}>{row[uid] || '—'}</Text>;
  };

  const renderContents = () => {
    if (loading || filteredRows.length === 0) {
      return (
        <NoDataPlaceholder
          noShadow
          noRound
          showRefreshButton={!loading && filteredRows.length === 0 && (!!selectedStatus || !!searchQuery)}
          refreshQuery={() => { setSelectedStatus(''); setSearchQuery(''); }}
          buttonHighlight={button.primaryBg}
          loading={loading}
          label="Loading payout setup status..."
          noDataLabel="No companies match your filters."
          textColor={page.primaryText}
          loaderColor={page.highlight}
        />
      );
    }

    return (
      <Box fill="horizontal" direction="column">
        {!small && (
          <Box
            fill="horizontal"
            direction="row"
            border={{
              color: page.tableBorder, size: '1px', style: 'solid', side: 'bottom',
            }}
          >
            {HEADER.map(({ uid, display, size }) => (
              <Box key={uid} width={size} pad={{ horizontal: '0.5rem', top: '1rem', bottom: '0.5rem' }}>
                {renderTableHeader(display, small)}
              </Box>
            ))}
          </Box>
        )}
        {filteredRows.map((row) => (
          <Box
            key={row.companyId}
            direction={small ? 'column' : 'row'}
            align={small ? 'start' : 'center'}
            border={{
              color: page.tableBorder, size: '1px', style: 'solid', side: 'bottom',
            }}
            pad={{ vertical: '1rem', right: '0.5rem' }}
            background={page.tableBg}
            fill="horizontal"
          >
            {HEADER.map(({ uid, display, size }) => (
              <Box
                key={uid}
                width={small ? '100%' : size}
                pad={{ horizontal: small ? '0' : '0.5rem', vertical: small ? '0.25rem' : '0' }}
                direction="row"
                gap="0.5rem"
              >
                {small && (
                  <Text size="0.875rem" color={page.tableHeader}>
                    {display}
                    :
                  </Text>
                )}
                {renderCell(uid, row)}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    );
  };

  const tableControls = (
    <Box wrap direction="row" gap="0.5rem" align="center">
      <Box pad={{ vertical: small ? '0.5rem' : '0' }}>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by name, ID or Stripe account..."
          style={{
            background: page.inputBg,
            color: page.primaryText,
            border: `1px solid ${page.inputBorder}`,
            borderRadius: '6px',
            padding: '0.3rem 0.6rem',
            fontSize: '0.85rem',
            outline: 'none',
            width: '18rem',
          }}
        />
      </Box>
      <Box pad={{ vertical: small ? '0.5rem' : '0' }}>
        <FlexibleSelectDropdown
          textColor={page.inputText}
          handleItemSelect={(value) => setSelectedStatus(value || '')}
          flexibleOptions={STATUS_OPTIONS}
          noSelectionLabel="All statuses"
          displayKey="display"
          valueKey="api"
          iconPath="images/filter-input-icon.svg"
          customPad="0.2rem 0.3rem"
          selectedOption={
            STATUS_OPTIONS.find((o) => o.api === selectedStatus)?.display || 'All statuses'
          }
          withReset={!!selectedStatus}
        />
      </Box>
      {(selectedStatus || searchQuery) && (
        <Box pad={{ vertical: small ? '0.5rem' : '0' }}>
          <AppButton
            overrideHover
            onClick={() => { setSelectedStatus(''); setSearchQuery(''); }}
            level="tableControls"
            height="var(--table-controls-height)"
            color={button.primaryText}
            bgColor={button.primaryBg}
            label="Reset"
          />
        </Box>
      )}
    </Box>
  );

  return (
    <AdminNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: 'Payout Setup Status',
        subTitle: 'Content ID · Admin',
      }}
    >
      <Seo />

      {labelsBreakdownRow && (
        <SimpleModal
          small={small}
          toggleModal={(open) => { if (!open) setLabelsBreakdownRow(null); }}
          modalTitle={`Asset Labels — ${labelsBreakdownRow.companyName}`}
          textColor={page.primaryText}
          highlightColor={page.highlight}
          modalBg={page.modalBg}
          closeLabel="Close"
          desktopMinWidth="36rem"
        >
          <Box pad={{ horizontal: '1.5rem', bottom: '1.5rem' }} gap="0.5rem">
            <Text size="0.85rem" color={page.secondaryText}>
              {`${labelsBreakdownRow.assetLabelCount} asset label${labelsBreakdownRow.assetLabelCount !== 1 ? 's' : ''} · ID: ${labelsBreakdownRow.companyId}`}
            </Text>
            <Box gap="0.4rem">
              {(labelsBreakdownRow.assetLabels || []).map((label, idx) => (
                <Box
                  key={label}
                  direction="row"
                  align="center"
                  gap="0.75rem"
                  pad={{ vertical: '0.4rem', horizontal: '0.6rem' }}
                  background={page.tableBg}
                  border={{ color: page.tableBorder, size: '1px' }}
                  round="6px"
                >
                  <Text size="0.75rem" color={page.tableHeader} weight={600}>
                    {idx + 1}
                  </Text>
                  <Text size="0.9rem" color={page.tableText}>{label}</Text>
                </Box>
              ))}
            </Box>
          </Box>
        </SimpleModal>
      )}

      <Box
        flex
        direction="column"
        background={usePageBg}
        pad={{ vertical: '1.5rem' }}
        height="100%"
        gap="1rem"
        width="100%"
      >
        <InfoNotice
          header="Payout setup status"
          text="Overview of all active CID companies and whether they have connected a Stripe account for payouts."
        />
        <Box
          direction={small ? 'column' : 'row'}
          gap="1rem"
          pad={{ horizontal: small ? '0' : '1rem' }}
        >
          {[
            { label: 'Total CID Companies', value: totalCompanies },
            { label: 'Setup Complete', value: completeCount },
            { label: 'Incomplete', value: incompleteCount },
            { label: 'Pending', value: pendingCount },
          ].map(({ label, value }) => (
            <Box
              key={label}
              flex
              background={page.altBg}
              border={{ color: page.highlight, size: '1px' }}
              round="8px"
              pad="1rem"
              gap="0.35rem"
            >
              <Text size="0.8rem" color={page.secondaryText} weight={500}>{label}</Text>
              <Text size="1.4rem" weight={700} color={page.primaryText}>{value}</Text>
            </Box>
          ))}
        </Box>
        <Box width="100%">
          <TableWrapper
            small={small}
            header="CID Companies"
            boxPad={tablePadding(small)}
            isPaged={false}
            totalResults={filteredRows.length}
            currentResults={filteredRows.length}
            tableControls={tableControls}
          >
            <Box fill="horizontal" direction="column">
              {renderContents()}
            </Box>
          </TableWrapper>
        </Box>
      </Box>
    </AdminNavWrapper>
  );
};

function mapStateToProps(state) {
  return {
    loading: state.fetchLoader.dataLoading,
    cidPayoutStatus: state.adminCidPayoutStatus.cidPayoutStatus,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      fetchCidPayoutStatus: initiateFetchCidPayoutSetupStatus,
      clearCidPayoutStatus: clearCidPayoutSetupStatus,
    },
    dispatch,
  );
}

AdminCidPayoutStatusPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  fetchCidPayoutStatus: PropTypes.func.isRequired,
  clearCidPayoutStatus: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  cidPayoutStatus: PropTypes.arrayOf(PropTypes.shape({
    companyId: PropTypes.number.isRequired,
    companyName: PropTypes.string.isRequired,
    stripePayoutStatus: PropTypes.oneOf(['complete', 'incomplete', 'pending']).isRequired,
    stripeAccountId: PropTypes.string,
    assetLabelCount: PropTypes.number.isRequired,
    assetLabels: PropTypes.arrayOf(PropTypes.string),
    featureEnabledAt: PropTypes.string,
  })),
};

export default connect(mapStateToProps, mapDispatchToProps)(AdminCidPayoutStatusPage);
